import type { ApiResponse } from './types';

// In production, API requests go directly to the Worker backend.
// VITE_API_URL must be set at build time (or here as a fallback).
// Fallback to Worker URL so requests never silently hit Pages itself (405).
const BASE = (import.meta.env.VITE_API_URL || 'https://bls-worker.service-a79.workers.dev') + '/api/v1';

function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

export function setToken(token: string): void {
  localStorage.setItem('auth_token', token);
}

export function clearToken(): void {
  localStorage.removeItem('auth_token');
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {};
  // Only set Content-Type when there is a request body
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    // If token is invalid/expired, clear it and redirect to login
    if (res.status === 401) {
      clearToken();
      window.location.href = '/login';
      return { ok: false, error: { code: 'UNAUTHORIZED', message: 'Session expired, please login again' } };
    }

    // Handle empty responses (204 No Content or zero content-length)
    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return { ok: true, data: undefined as unknown as T };
    }

    const text = await res.text();
    if (!text) {
      return { ok: true, data: undefined as unknown as T };
    }
    let json: unknown;
    try {
      json = JSON.parse(text);
    } catch {
      return { ok: false, error: { code: 'PARSE_ERROR', message: 'Invalid response from server' } };
    }
    return json as ApiResponse<T>;
  } catch (err) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'Network error',
      },
    };
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ─── Auth ──────────────────────────────────────────────────────────────────
import type { User } from './types';

export const authApi = {
  register: (email: string, password: string, displayName: string) =>
    api.post<{ token: string; user: User }>('/auth/register', { email, password, displayName }),
  login: (email: string, password: string) =>
    api.post<{ token: string; user: User }>('/auth/login', { email, password }),
  me: () => api.get<User>('/auth/me'),
};

// ─── Projects ─────────────────────────────────────────────────────────────
import type { Project } from './types';

export const projectsApi = {
  list: () => api.get<Project[]>('/projects'),
  create: (name: string, description?: string) =>
    api.post<Project>('/projects', { name, description }),
};

// ─── Teams ────────────────────────────────────────────────────────────────
import type { Team, TeamTemplate } from './types';

export const teamsApi = {
  list: (projectId: string) => api.get<Team[]>(`/teams?projectId=${projectId}`),
  create: (projectId: string, name: string) =>
    api.post<Team>('/teams', { projectId, name }),
  fromTemplate: (projectId: string, templateId: string, name: string) =>
    api.post<Team>('/teams/from-template', { projectId, templateId, name }),
  templates: () => api.get<TeamTemplate[]>('/teams/templates'),
};

// ─── Agents ───────────────────────────────────────────────────────────────
import type { Agent, AgentMemory, AgentStatus } from './types';

export const agentsApi = {
  list: (projectId: string) => api.get<Agent[]>(`/agents?projectId=${projectId}`),
  create: (data: {
    displayName: string;
    role: string;
    projectId: string;
    teamId?: string;
    llmProvider?: string;
    llmModel?: string;
    description?: string;
    systemPrompt?: string;
  }) => api.post<Agent>('/agents', data),
  update: (id: string, data: Partial<Agent>) => api.patch<Agent>(`/agents/${id}`, data),
  delete: (id: string) => api.delete<void>(`/agents/${id}`),
  setStatus: (id: string, status: AgentStatus) =>
    api.patch<Agent>(`/agents/${id}/status`, { status }),
  getMemory: (id: string) => api.get<AgentMemory>(`/agents/${id}/memory`),
  updateMemory: (id: string, content: string) =>
    api.put<AgentMemory>(`/agents/${id}/memory`, { content }),
  clearMemory: (id: string) => api.delete<void>(`/agents/${id}/memory`),
};

// ─── Channels ─────────────────────────────────────────────────────────────
import type { Channel, Message } from './types';

export const channelsApi = {
  list: (projectId: string) => api.get<Channel[]>(`/channels?projectId=${projectId}`),
  create: (projectId: string, name: string, type?: string) =>
    api.post<Channel>('/channels', { projectId, name, type: type ?? 'standard' }),
  messages: (channelId: string) => api.get<Message[]>(`/channels/${channelId}/messages`),
  poll: (channelIds: string[], since?: string) => {
    const params = new URLSearchParams();
    params.set('channels', channelIds.join(','));
    if (since) params.set('since', since);
    return api.get<{ events: Message[]; last_cursor: string | null; poll_interval_ms: number; has_more: boolean }>(
      `/polling?${params.toString()}`,
    );
  },
  sendMessage: (
    channelId: string,
    content: string,
    options?: {
      type?: string;
      routingPolicy?: string;
      targetAgentId?: string;
      threadParentId?: string;
    },
  ) => api.post<Message>(`/channels/${channelId}/messages`, { content, ...options }),
};

// ─── Tasks ────────────────────────────────────────────────────────────────
import type { Task, TaskStatus } from './types';

export const tasksApi = {
  list: (projectId: string) => api.get<Task[]>(`/tasks?projectId=${projectId}`),
  create: (projectId: string, title: string, description?: string) =>
    api.post<Task>('/tasks', { projectId, title, description }),
  updateStatus: (id: string, status: TaskStatus) =>
    api.patch<Task>(`/tasks/${id}/status`, { status }),
  claim: (id: string) => api.post<Task>(`/tasks/${id}/claim`, {}),
};

// ─── Skills ───────────────────────────────────────────────────────────────
import type { Skill } from './types';

export const skillsApi = {
  list: () => api.get<Skill[]>('/skills'),
  install: (agentId: string, skillId: string) =>
    api.post<void>(`/skills/agents/${agentId}/install`, { skillId }),
};
